import {
    createContext,
    useContext,
    useState,
    useCallback,
    useRef,
    useEffect,
    type ReactNode,
} from 'react';
import { toast } from 'sonner';
import type { ChatMessage, ChatMessagePart, Conversation, ChatStatus } from './types';
import {
    streamChat,
    applyStreamEvent,
    createStreamReducerState,
    isTerminalEvent,
} from './stream-consumer';
import {
    fetchConversations,
    createConversation as apiCreateConversation,
    getConversation as apiGetConversation,
    deleteConversation as apiDeleteConversation,
    saveMessage as apiSaveMessage,
    updateConversation as apiUpdateConversation,
    deleteMessage as apiDeleteMessage,
    forkConversation as apiForkConversation,
} from './chat-api';

export interface BranchGroup {
    branches: ChatMessage[][];
    activeBranch: number;
}

export type BranchMap = Record<string, BranchGroup>;

type MessageSendStatus = 'sending' | 'confirmed' | 'failed';

export interface QueuedMessage {
    id: string;
    text: string;
    queuedAt: number;
}

interface ChatContextValue {
    conversations: Conversation[];
    activeConversationId: string | null;
    messages: ChatMessage[];
    selectedCapability: string;
    status: ChatStatus;
    isSidebarOpen: boolean;
    branchMap: BranchMap;
    customInstructions: string;
    conversationSystemPrompt: string;
    messageSendStatuses: Record<string, MessageSendStatus>;
    isOffline: boolean;
    queuedMessages: QueuedMessage[];
    rateLimitedUntil: number | null;

    setActiveConversation: (id: string | null) => void;
    setSelectedCapability: (ref: string) => void;
    setMessages: (messages: ChatMessage[]) => void;
    setConversations: (conversations: Conversation[]) => void;
    toggleSidebar: () => void;
    setSidebarOpen: (open: boolean) => void;
    setCustomInstructions: (instructions: string) => void;
    setConversationSystemPrompt: (prompt: string) => void;

    sendMessage: (text: string) => Promise<void>;
    editMessage: (messageId: string, newText: string) => Promise<void>;
    deleteMessage: (messageId: string, deleteBelow?: boolean) => Promise<void>;
    retryMessage: (messageId: string, options?: { capabilityRef?: string }) => Promise<void>;
    forkConversation: (atMessageId: string) => Promise<void>;
    retrySendMessage: (messageId: string) => Promise<void>;
    removeFromQueue: (id: string) => void;
    switchBranch: (forkParentId: string, branchIndex: number) => void;
    stopStreaming: () => void;
    loadConversations: () => Promise<void>;
    deleteConversation: (id: string) => Promise<void>;
    renameConversation: (id: string, title: string) => Promise<void>;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function useChat() {
    const ctx = useContext(ChatContext);
    if (!ctx) throw new Error('useChat must be used within ChatProvider');
    return ctx;
}

export function ChatProvider({ children }: { children: ReactNode }) {
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [selectedCapability, setSelectedCapability] = useState('');
    const [status, setStatus] = useState<ChatStatus>('ready');
    const [isSidebarOpen, setSidebarOpen] = useState(true);
    const [branchMap, setBranchMap] = useState<BranchMap>({});
    const [customInstructions, setCustomInstructions] = useState('');
    const [conversationSystemPrompt, setConversationSystemPrompt] = useState('');
    const [messageSendStatuses, setMessageSendStatuses] = useState<Record<string, MessageSendStatus>>({});
    const [isOffline, setIsOffline] = useState(false);
    const [queuedMessages, setQueuedMessages] = useState<QueuedMessage[]>([]);
    const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const pendingRetriesRef = useRef<Map<string, { text: string; convId: string }>>(new Map());
    const processingQueueRef = useRef(false);
    const selectedCapRef = useRef(selectedCapability);

    useEffect(() => {
        selectedCapRef.current = selectedCapability;
    }, [selectedCapability]);

    const setSelectedCapabilityWithRef = useCallback((ref: string) => {
        selectedCapRef.current = ref;
        setSelectedCapability(ref);
    }, []);

    useEffect(() => {
        const goOnline = () => setIsOffline(false);
        const goOffline = () => setIsOffline(true);
        setIsOffline(!navigator.onLine);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, []);

    const loadConversations = useCallback(async () => {
        try {
            const res = await fetchConversations();
            setConversations(res.data);
        } catch {
            // silent
        }
    }, []);

    useEffect(() => {
        loadConversations();
    }, [loadConversations]);

    const setActiveConversation = useCallback(async (id: string | null) => {
        setActiveConversationId(id);
        if (!id) {
            setMessages([]);
            setBranchMap({});
            setConversationSystemPrompt('');
            return;
        }
        try {
            const data = await apiGetConversation(id);
            const loaded: ChatMessage[] = (data.messages ?? []).map((m: any) => ({
                id: m.id,
                conversationId: m.conversation_id,
                role: m.role,
                parts: Array.isArray(m.content) ? m.content : [{ type: 'text', text: JSON.stringify(m.content) }],
                createdAt: m.created,
                parentMessageId: m.parent_message_id,
                branchIndex: m.branch_index,
            }));
            setMessages(loaded);
            setBranchMap({});
            if (data.conversation.capability_ref) {
                setSelectedCapabilityWithRef(data.conversation.capability_ref);
            }
        } catch {
            setMessages([]);
        }
    }, [setSelectedCapabilityWithRef]);

    const toggleSidebar = useCallback(() => setSidebarOpen((p) => !p), []);

    const stopStreaming = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        setStatus('ready');
    }, []);

    const streamResponse = useCallback(
        async (
            convId: string,
            historyMessages: ChatMessage[],
            assistantId: string,
            capabilityRef: string,
        ) => {
            const controller = new AbortController();
            abortRef.current = controller;

            try {
                const stream = streamChat(
                    {
                        conversationId: convId,
                        capabilityRef,
                        method: 'chat',
                        messages: historyMessages.map((m) => ({
                            id: m.id,
                            role: m.role,
                            parts: m.parts,
                        })),
                    },
                    controller.signal,
                );

                setStatus('streaming');
                const reducerState = createStreamReducerState();

                for await (const event of stream) {
                    const newParts = applyStreamEvent(event, reducerState);
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === assistantId ? { ...m, parts: newParts } : m,
                        ),
                    );

                    if (isTerminalEvent(event)) break;
                }

                const finalParts = reducerState.parts;
                apiSaveMessage(convId, {
                    id: assistantId,
                    role: 'assistant',
                    content: finalParts,
                }).catch(() => {});
            } catch (err: any) {
                const isAbort = err?.name === 'AbortError';
                if (isAbort) {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === assistantId
                                ? {
                                      ...m,
                                      parts: m.parts.length > 0
                                          ? [...m.parts, { type: 'status', message: 'Generation stopped', state: 'info' as const }]
                                          : [{ type: 'status', message: 'Generation stopped', state: 'info' as const }],
                                  }
                                : m,
                        ),
                    );
                } else {
                    const errMsg = err?.message ?? 'Stream failed';
                    toast.error(errMsg);
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === assistantId
                                ? { ...m, parts: [...m.parts, { type: 'error', code: 'STREAM_ERROR', message: errMsg }] }
                                : m,
                        ),
                    );
                }
            } finally {
                abortRef.current = null;
                setStatus('ready');
            }
        },
        [customInstructions, conversationSystemPrompt],
    );

    const sendMessageDirect = useCallback(
        async (text: string) => {
            const capRef = selectedCapRef.current;
            if (!capRef) {
                toast.error('Please select a capability first');
                return;
            }

            let convId = activeConversationId;

            if (!convId) {
                try {
                    const conv = await apiCreateConversation(text.slice(0, 60), capRef);
                    convId = conv.id;
                    setActiveConversationId(convId);
                    setConversations((prev) => [conv, ...prev]);
                } catch {
                    toast.error('Failed to create conversation');
                    return;
                }
            }

            const optimisticId = crypto.randomUUID();
            const userMsg: ChatMessage = {
                id: optimisticId,
                conversationId: convId,
                role: 'user',
                parts: [{ type: 'text', text }],
                createdAt: new Date().toISOString(),
            };

            const assistantId = crypto.randomUUID();
            const assistantMsg: ChatMessage = {
                id: assistantId,
                conversationId: convId,
                role: 'assistant',
                parts: [],
                createdAt: new Date().toISOString(),
                capabilityRef: capRef,
            };

            setMessageSendStatuses((prev) => ({ ...prev, [optimisticId]: 'sending' }));
            pendingRetriesRef.current.set(optimisticId, { text, convId });

            const allMessages = [...messages, userMsg];
            setMessages((prev) => [...prev, userMsg, assistantMsg]);
            setStatus('submitted');

            apiSaveMessage(convId, {
                id: userMsg.id,
                role: 'user',
                content: userMsg.parts,
            }).catch(() => {});

            try {
                await streamResponse(convId, allMessages, assistantId, capRef);
                setMessageSendStatuses((prev) => ({ ...prev, [optimisticId]: 'confirmed' }));
                pendingRetriesRef.current.delete(optimisticId);

                if (messages.length === 0) {
                    const title = text.slice(0, 60) + (text.length > 60 ? '...' : '');
                    apiUpdateConversation(convId, title, capRef).catch(() => {});
                    setConversations((prev) =>
                        prev.map((c) => (c.id === convId ? { ...c, title } : c)),
                    );
                }
            } catch {
                setMessageSendStatuses((prev) => ({ ...prev, [optimisticId]: 'failed' }));
                toast.error('Failed to send message. Click retry to try again.');
            }
        },
        [activeConversationId, messages, streamResponse],
    );

    const processQueue = useCallback(async () => {
        if (processingQueueRef.current) return;
        processingQueueRef.current = true;
        try {
            while (true) {
                const next = queuedMessages[0];
                if (!next) break;
                setQueuedMessages((prev) => prev.slice(1));

                if (rateLimitedUntil && rateLimitedUntil > Date.now()) {
                    const wait = rateLimitedUntil - Date.now();
                    await new Promise((r) => setTimeout(r, wait));
                    setRateLimitedUntil(null);
                }

                await sendMessageDirect(next.text);
            }
        } finally {
            processingQueueRef.current = false;
        }
    }, [queuedMessages, rateLimitedUntil, sendMessageDirect]);

    useEffect(() => {
        if (status === 'ready' && queuedMessages.length > 0 && !processingQueueRef.current) {
            processQueue();
        }
    }, [status, queuedMessages, processQueue]);

    const sendMessage = useCallback(
        async (text: string) => {
            if (isOffline) {
                toast.error('You are offline. Message will be sent when you reconnect.');
                return;
            }

            const isCurrentlyStreaming = status === 'streaming' || status === 'submitted';
            if (isCurrentlyStreaming) {
                setQueuedMessages((prev) => [
                    ...prev,
                    { id: crypto.randomUUID(), text, queuedAt: Date.now() },
                ]);
                toast.info('Message queued — will send after current response completes.');
                return;
            }

            await sendMessageDirect(text);
        },
        [isOffline, status, sendMessageDirect],
    );

    const editMessage = useCallback(
        async (messageId: string, newText: string) => {
            const msgIndex = messages.findIndex((m) => m.id === messageId);
            if (msgIndex < 0 || messages[msgIndex].role !== 'user') return;

            const convId = activeConversationId ?? crypto.randomUUID();
            const forkParentId = msgIndex > 0 ? messages[msgIndex - 1].id : '__root__';
            const currentBranchFromFork = messages.slice(msgIndex);
            const messagesBeforeFork = messages.slice(0, msgIndex);

            setBranchMap((prev) => {
                const group = prev[forkParentId];
                if (group) {
                    const updatedBranches = [...group.branches];
                    updatedBranches[group.activeBranch] = currentBranchFromFork;
                    return {
                        ...prev,
                        [forkParentId]: {
                            branches: [...updatedBranches, []],
                            activeBranch: updatedBranches.length,
                        },
                    };
                }
                return {
                    ...prev,
                    [forkParentId]: {
                        branches: [currentBranchFromFork, []],
                        activeBranch: 1,
                    },
                };
            });

            const newUserMessage: ChatMessage = {
                id: crypto.randomUUID(),
                conversationId: convId,
                role: 'user',
                createdAt: new Date().toISOString(),
                parts: [{ type: 'text', text: newText }],
                parentMessageId: forkParentId === '__root__' ? undefined : forkParentId,
            };

            const capRef = selectedCapRef.current;
            const assistantId = crypto.randomUUID();
            const assistantMessage: ChatMessage = {
                id: assistantId,
                conversationId: convId,
                role: 'assistant',
                createdAt: new Date().toISOString(),
                parts: [],
                capabilityRef: capRef,
            };

            const allMessages = [...messagesBeforeFork, newUserMessage];
            setMessages([...messagesBeforeFork, newUserMessage, assistantMessage]);
            setStatus('submitted');

            await streamResponse(convId, allMessages, assistantId, capRef);
        },
        [activeConversationId, messages, streamResponse],
    );

    const deleteMessage = useCallback(
        async (messageId: string, deleteBelow = false) => {
            const msgIndex = messages.findIndex((m) => m.id === messageId);
            if (msgIndex < 0) return;

            if (deleteBelow) {
                setMessages((prev) => prev.slice(0, msgIndex));
            } else {
                setMessages((prev) => prev.filter((m) => m.id !== messageId));
            }

            if (activeConversationId) {
                apiDeleteMessage(activeConversationId, messageId, deleteBelow).catch((err) => {
                    console.error('[chat-provider:deleteMessage]', err);
                    toast.error('Failed to delete message from server');
                });
            }
        },
        [activeConversationId, messages],
    );

    const switchBranch = useCallback(
        (forkParentId: string, targetIndex: number) => {
            const group = branchMap[forkParentId];
            if (!group || targetIndex === group.activeBranch) return;

            let forkIndex: number;
            if (forkParentId === '__root__') {
                forkIndex = 0;
            } else {
                forkIndex = messages.findIndex((m) => m.id === forkParentId);
                if (forkIndex < 0) return;
                forkIndex += 1;
            }

            const currentBranchMessages = messages.slice(forkIndex);
            const targetBranch = group.branches[targetIndex];
            if (!targetBranch) return;

            const updatedBranches = [...group.branches];
            updatedBranches[group.activeBranch] = currentBranchMessages;

            setBranchMap((prev) => ({
                ...prev,
                [forkParentId]: {
                    branches: updatedBranches,
                    activeBranch: targetIndex,
                },
            }));

            setMessages((prev) => [...prev.slice(0, forkIndex), ...targetBranch]);
        },
        [messages, branchMap],
    );

    const retryMessage = useCallback(
        async (messageId: string, options?: { capabilityRef?: string }) => {
            const msgIndex = messages.findIndex((m) => m.id === messageId);
            if (msgIndex < 0 || messages[msgIndex].role !== 'assistant') return;

            const convId = activeConversationId ?? crypto.randomUUID();
            const capRef = options?.capabilityRef ?? selectedCapRef.current;

            const forkParentId = msgIndex > 1 ? messages[msgIndex - 1].id : '__root__';
            const currentBranchFromAssistant = messages.slice(msgIndex);
            const messagesBeforeAssistant = messages.slice(0, msgIndex);

            setBranchMap((prev) => {
                const group = prev[forkParentId];
                if (group) {
                    const updatedBranches = [...group.branches];
                    updatedBranches[group.activeBranch] = currentBranchFromAssistant;
                    return {
                        ...prev,
                        [forkParentId]: {
                            branches: [...updatedBranches, []],
                            activeBranch: updatedBranches.length,
                        },
                    };
                }
                return {
                    ...prev,
                    [forkParentId]: {
                        branches: [currentBranchFromAssistant, []],
                        activeBranch: 1,
                    },
                };
            });

            const assistantId = crypto.randomUUID();
            const assistantMessage: ChatMessage = {
                id: assistantId,
                conversationId: convId,
                role: 'assistant',
                createdAt: new Date().toISOString(),
                parts: [],
                capabilityRef: capRef,
            };

            setMessages([...messagesBeforeAssistant, assistantMessage]);
            setStatus('submitted');

            const prevCapability = selectedCapability;
            if (options?.capabilityRef) {
                setSelectedCapabilityWithRef(options.capabilityRef);
            }

            await streamResponse(convId, messagesBeforeAssistant, assistantId, capRef);

            if (options?.capabilityRef) {
                setSelectedCapabilityWithRef(prevCapability);
            }
        },
        [activeConversationId, selectedCapability, messages, streamResponse, setSelectedCapabilityWithRef],
    );

    const forkConversationFn = useCallback(
        async (atMessageId: string) => {
            if (!activeConversationId) {
                toast.error('No active conversation to fork');
                return;
            }

            try {
                const result = await apiForkConversation(activeConversationId, atMessageId);
                toast.success('Conversation forked');
                window.location.href = `/chat/${result.newConversationId}`;
            } catch (error) {
                const msg = error instanceof Error ? error.message : 'Failed to fork conversation';
                toast.error(msg);
            }
        },
        [activeConversationId],
    );

    const removeFromQueue = useCallback((id: string) => {
        setQueuedMessages((prev) => prev.filter((m) => m.id !== id));
    }, []);

    const retrySendMessage = useCallback(
        async (messageId: string) => {
            const pending = pendingRetriesRef.current.get(messageId);
            if (!pending) return;

            setMessages((prev) =>
                prev.filter(
                    (m) => m.id !== messageId && !(m.role === 'assistant' && m.parts.length === 0),
                ),
            );
            setMessageSendStatuses((prev) => {
                const next = { ...prev };
                delete next[messageId];
                return next;
            });
            pendingRetriesRef.current.delete(messageId);

            await sendMessage(pending.text);
        },
        [sendMessage],
    );

    const deleteConversation = useCallback(
        async (id: string) => {
            try {
                await apiDeleteConversation(id);
                setConversations((prev) => prev.filter((c) => c.id !== id));
                if (activeConversationId === id) {
                    setActiveConversationId(null);
                    setMessages([]);
                    setBranchMap({});
                }
            } catch {
                toast.error('Failed to delete conversation');
            }
        },
        [activeConversationId],
    );

    const renameConversation = useCallback(
        async (id: string, title: string) => {
            try {
                const conv = conversations.find((c) => c.id === id);
                await apiUpdateConversation(id, title, conv?.capability_ref ?? '');
                setConversations((prev) =>
                    prev.map((c) => (c.id === id ? { ...c, title } : c)),
                );
            } catch {
                toast.error('Failed to rename conversation');
            }
        },
        [conversations],
    );

    return (
        <ChatContext.Provider
            value={{
                conversations,
                activeConversationId,
                messages,
                selectedCapability,
                status,
                isSidebarOpen,
                branchMap,
                customInstructions,
                conversationSystemPrompt,
                messageSendStatuses,
                isOffline,
                queuedMessages,
                rateLimitedUntil,
                setActiveConversation,
                setSelectedCapability: setSelectedCapabilityWithRef,
                setMessages,
                setConversations,
                toggleSidebar,
                setSidebarOpen,
                setCustomInstructions,
                setConversationSystemPrompt,
                sendMessage,
                editMessage,
                deleteMessage,
                retryMessage,
                forkConversation: forkConversationFn,
                retrySendMessage,
                removeFromQueue,
                switchBranch,
                stopStreaming,
                loadConversations,
                deleteConversation,
                renameConversation,
            }}
        >
            {children}
        </ChatContext.Provider>
    );
}
